// services/supplierPaymentService.js
import axios from 'axios';
import { API_BASE_URL, API_URLS } from '../config/constants';

// Create axios instance with default headers
const api = axios.create({
  baseURL: API_BASE_URL,
});

// Add request interceptor to include token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    config.headers['Content-Type'] = 'application/json';
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

const supplierPaymentService = {
  // Get all payments with pagination
  getAllPayments: async (params = {}) => {
    try {
      const response = await api.get(API_URLS.SUPPLIER_PAYMENTS, {
        params: {
          page: params.page || 1,
          per_page: params.per_page || 15,
          ...params
        }
      });
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get payment by ID
  getPaymentById: async (paymentId) => {
    try {
      const response = await api.get(API_URLS.SUPPLIER_PAYMENT_BY_ID(paymentId));
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Create new payment
  createPayment: async (paymentData) => {
    try {
      const response = await api.post(API_URLS.SUPPLIER_PAYMENTS, paymentData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Update payment
  updatePayment: async (paymentId, paymentData) => {
    try {
      const response = await api.put(API_URLS.SUPPLIER_PAYMENT_BY_ID(paymentId), paymentData);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Delete payment
  deletePayment: async (paymentId) => {
    try {
      const response = await api.delete(API_URLS.SUPPLIER_PAYMENT_BY_ID(paymentId));
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get payments by supplier
  getPaymentsBySupplier: async (supplierId) => {
    try {
      const response = await api.get(API_URLS.SUPPLIER_PAYMENTS_BY_SUPPLIER(supplierId));
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get completed payments
  getCompletedPayments: async () => {
    try {
      const response = await api.get(API_URLS.COMPLETED_PAYMENTS);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get purchase orders for a supplier (for payment)
  getSupplierPurchaseOrders: async (supplierId) => {
    try {
      const response = await api.get(API_URLS.SUPPLIER_PURCHASE_ORDERS_FOR_PAYMENT(supplierId));
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get payment summary for a purchase order
  getPurchaseOrderPaymentSummary: async (purchaseOrderId) => {
    try {
      const response = await api.get(API_URLS.PURCHASE_ORDER_PAYMENT_SUMMARY(purchaseOrderId));
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get supplier payment summary
  getSupplierPaymentSummary: async (supplierId) => {
    try {
      const response = await api.get(API_URLS.SUPPLIER_PAYMENT_SUMMARY(supplierId));
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get pending payments report
  getPendingPaymentsReport: async () => {
    try {
      const response = await api.get(API_URLS.SUPPLIER_PENDING_PAYMENTS_REPORT);
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },

  // Get pending payments for a specific supplier
  getSupplierPendingPayments: async (supplierId) => {
    try {
      const response = await api.get(API_URLS.SUPPLIER_PENDING_PAYMENTS(supplierId));
      return response.data;
    } catch (error) {
      throw error.response?.data || error.message;
    }
  },
};

export default supplierPaymentService;